<template>
  <div class="payment-status-page">
    <PaymentSuccess v-if="showSuccess" />
    <PaymentFailed v-else-if="showFailure" />
    <InspectionOutcome v-else-if="showInspection" />
    <RequirementsPending v-else-if="showRequirements" />
    <div v-else class="wrapper">
      <div class="inner-wrapper">
        <div
          class="payment-status-top-bar d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mx-3 mx-md-5 my-3 gap-2 gap-md-0"
        >
          <div class="d-flex align-items-center w-100 w-md-auto">
            <div class="flex-shrink-0" v-if="insurerLogoSrc">
              <img :src="insurerLogoSrc" alt="" class="payment-status-insurer-logo" />
            </div>
            <div class="ms-3 ms-md-4" v-if="productName">
              <h4 class="mb-0">{{ productName }}</h4>
            </div>
          </div>
          <div class="propno w-100 w-md-auto">
            <JourneyIdContainer
              v-if="journeyId"
              :journey-id="String(journeyId)"
              label="JOURNEY ID :"
            />
          </div>
        </div>
        <div class="payment-status-content text-center">
          <VerifyingStatusPanel
            v-if="!showError && !paymentPending"
            :title="pageTitle"
            :subtitle="pageSubtitle"
            :progress-percent="progressPercent"
            :progress-label="statusHint"
            :steps="verifyingSteps"
            :active-step-index="activeStepIndex"
          />
          <template v-else-if="!showError && paymentPending">
            <h3 class="payment-status-title">{{ pageTitle }}</h3>
            <h6 class="base-reg payment-status-subtitle" v-html="pageSubtitle"></h6>
          </template>
          <template v-else>
            <h3 class="payment-status-title payment-status-title--error">{{ pageTitle }}</h3>
            <h6
              class="base-reg payment-status-subtitle payment-status-subtitle--error"
              v-html="pageSubtitle"
            ></h6>
            <div class="payment-status-error-actions mt-4">
              <button type="button" class="btn btn-primary" @click="goToDiy">
                {{ diyButtonLabel }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VerifyingStatusPanel from '@/components/common/VerifyingStatusPanel.vue';
import JourneyIdContainer from '@/components/common/JourneyIdContainer.vue';
import PaymentSuccess from '@/components/pageLoaders/paymentSuccess.vue';
import PaymentFailed from '@/components/pageLoaders/paymentFailed.vue';
import InspectionOutcome from '@/components/pageLoaders/thankYou.vue';
import RequirementsPending from '@/components/pageLoaders/paymentPending.vue';
import { stripHtml } from '@/utils/inputSanitizer';
import { shouldSkipGodigitKycOvd } from '@/utils/godigitKycOvdSkip';
import { classifyPaymentDetailsStatus } from '@/lib/payment/classify-payment-details-status';
import { TERMINAL_OUTCOMES } from '@/lib/payment/navigate-payment-outcome';
import {
  DIY_CHECK_PATH,
  DIY_INLINE_ERROR_BUTTON_LABEL,
  DIY_INLINE_ERROR_TITLE,
  PAYMENT_DETAILS_POLL_INTERVAL_MS,
  PAYMENT_DETAILS_POLL_TIMEOUT_MS,
  PAYMENT_DETAILS_STAGE,
  PAYMENT_GATEWAY_FALLBACK_MESSAGE,
  PAYMENT_PENDING_QUERY_ACTION,
  PAYMENT_PENDING_QUERY_DESCRIPTION,
  PAYMENT_PENDING_QUERY_STATUS,
  PAYMENT_PENDING_SUBTITLE,
  PAYMENT_PENDING_TITLE,
  PAYMENT_STATUS_INITIAL_DELAY_MS,
  PAYMENT_STATUS_OUTCOME_FAILURE,
  PAYMENT_STATUS_OUTCOME_INSPECTION,
  PAYMENT_STATUS_OUTCOME_PENDING,
  PAYMENT_STATUS_OUTCOME_QUERY_KEY,
  PAYMENT_STATUS_OUTCOME_REQUIREMENTS,
  PAYMENT_STATUS_OUTCOME_STORAGE_KEY,
  PAYMENT_STATUS_OUTCOME_SUCCESS,
  PAYMENT_STATUS_PATH,
  VERIFYING_STEP_CONFIRMING_PAYMENT,
  VERIFYING_STEP_SUBMITTING,
  resolvePaymentStatusActiveStepIndex,
  resolvePaymentStatusVerifyingSteps,
} from '@/lib/payment/payment-status-constants';

const CONFIRMING_HINT = `${VERIFYING_STEP_CONFIRMING_PAYMENT}…`;
const SUBMITTING_HINT = `${VERIFYING_STEP_SUBMITTING}…`;
const PAYMENT_STATUS_TITLE = 'Verifying the status';
const PAYMENT_STATUS_SUBTITLE =
  'Please do not refresh or close this window while we confirm your payment.';

export default {
  name: 'PaymentStatusPage',
  components: { VerifyingStatusPanel,
    JourneyIdContainer,
    PaymentSuccess,
    PaymentFailed,
    InspectionOutcome,
    RequirementsPending,
  },
  data() {
    return {
      progressPercent: 0,
      remainingMs: PAYMENT_DETAILS_POLL_TIMEOUT_MS,
      progressTimer: null,
      statusHint: CONFIRMING_HINT,
      verifyingStage: 'confirming_payment',
      journeyId: '',
      configData: null,
      selectedPlan: null,
      payResponse: null,
      payment_status: '',
      policyNo: '',
      inspectionLink: '',
      applicationNo: '',
      errorMsgs: '',
      tpv: '',
      uiQuoteId: '',
      prePaymentFlow: false,
      postPaymentFlow: false,
      proposalSubmitted: false,
      paymentPending: false,
      showingProposalSubmit: false,
      completingAfterPayment: false,
      showSuccess: false,
      showFailure: false,
      showInspection: false,
      showRequirements: false,
      showError: false,
      errorMessage: '',
      diyButtonLabel: DIY_INLINE_ERROR_BUTTON_LABEL,
    };
  },
  async mounted() {
    this.restoreSessionFromStorage();

    const persistedOutcome = this.resolvePersistedOutcome();
    if (TERMINAL_OUTCOMES.includes(persistedOutcome)) {
      this.applyOutcomeView(persistedOutcome);
      this.syncOutcomeQuery(persistedOutcome);
      return;
    }

    this.startProgressBar();
    this.parsePayResponseFromQuery();

    await this.loadFlowMode();
    this.verifyingStage = 'confirming_payment';
    this.statusHint = CONFIRMING_HINT;
    await this.wait(PAYMENT_STATUS_INITIAL_DELAY_MS);

    const paymentIntimationSetting = this.configData?.PAYMENT_INTIMATION;
    const plan = this.planForDisplay;
    let payment_status_mode =
      paymentIntimationSetting?.[plan?.insuranceAndProducts?.productCode];
    if (!payment_status_mode) {
      payment_status_mode =
        paymentIntimationSetting?.[plan?.insuranceAndProducts?.insuranceCompanyCode];
    }
    if (payment_status_mode === 'yes') {
      await this.paymentIntimation();
    }

    await this.pollPaymentDetailsAndRedirect();
  },
  beforeUnmount() {
    this.stopProgressBar();
  },
  computed: {
    verifyingSteps() {
      return resolvePaymentStatusVerifyingSteps(!this.prePaymentFlow);
    },
    activeStepIndex() {
      return resolvePaymentStatusActiveStepIndex({
        verifyingStage: this.verifyingStage,
        isPostPayment: !this.prePaymentFlow,
      });
    },
    productName() {
      return (
        this.planForDisplay?.insuranceAndProducts?.productName
        || this.planForDisplay?.insuranceAndProducts?.policyTypeDesc
        || ''
      );
    },
    insurerLogoSrc() {
      return this.planForDisplay?.supportingData?.insCompanyLogo || '';
    },
    pageTitle() {
      if (this.showError) {
        return DIY_INLINE_ERROR_TITLE;
      }
      if (this.paymentPending) {
        return PAYMENT_PENDING_TITLE;
      }
      return PAYMENT_STATUS_TITLE;
    },
    pageSubtitle() {
      if (this.showError) {
        return this.errorMessage || PAYMENT_GATEWAY_FALLBACK_MESSAGE;
      }
      if (this.paymentPending) {
        return PAYMENT_PENDING_SUBTITLE;
      }
      return PAYMENT_STATUS_SUBTITLE;
    },
  },
  methods: {
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    startProgressBar() {
      this.stopProgressBar();
      const startedAt = Date.now();
      this.progressPercent = 0;
      this.remainingMs = PAYMENT_DETAILS_POLL_TIMEOUT_MS;
      this.progressTimer = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, PAYMENT_DETAILS_POLL_TIMEOUT_MS - elapsed);
        this.remainingMs = remaining;
        const pct = Math.min(100, (elapsed / PAYMENT_DETAILS_POLL_TIMEOUT_MS) * 100);
        this.progressPercent = Math.round(pct * 10) / 10;
        if (remaining <= 0) {
          this.stopProgressBar();
        }
      }, 200);
    },
    stopProgressBar() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    isProposalSubmittedFlag(value) {
      return [true, 'true', 1, '1'].includes(value);
    },
    getCurrentDateAndTime() {
      const today = new Date();
      let month = today.getMonth() + 1;
      let date = today.getDate();
      if (month < 10) {
        month = `0${month}`;
      }
      if (date < 10) {
        date = `0${date}`;
      }
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      return `${today.getFullYear()}-${month}-${date} ${time}`;
    },
    resolvePayReferenceId() {
      const pay = this.payResponse || {};
      return (
        pay.referenceId
        || pay.reference_id
        || pay.transactionId
        || pay.transaction_id
        || ''
      );
    },
    async paymentIntimation() {
      try {
        const referenceId = this.resolvePayReferenceId();
        if (!referenceId) {
          return;
        }
        const amount =
          this.tpv
          || this.planForDisplay?.productDetails?.totalPremiumDetails?.[0]?.totalPremiumWithTax
          || this.planForDisplay?.totalPremiumDetails?.[0]?.totalPremiumWithTax
          || '';
        const qData = {
          journey_id: this.journeyId,
          payment_mode: 'UPI',
          currency_name: 'INR',
          consumer_reference_no: referenceId,
          status: 'Success',
          transaction_id: referenceId,
          amount: String(amount),
          txn_date: this.getCurrentDateAndTime(),
        };
        const headers = {
          quoteId: this.uiQuoteId,
          Authorization: `Basic ${window.btoa(process.env.VUE_APP_DATA_PER_USERNPASS)}`,
          'Content-Type': 'application/json',
        };
        const response = await this.backendApi('paymentIntimation', qData, headers);
        if (response?.data?.errors?.length > 0 || response === 'error' || response?.status === 'error') {
          return;
        }
        const policyNumber = response?.data?.data?.data?.trackInfo?.policyNumber;
        if (policyNumber) {
          this.policyNo = policyNumber;
          sessionStorage.setItem('policyNo', this.policyNo);
        }
      } catch (error) {
        console.error('Error in paymentIntimation:', error);
      }
    },
    async loadFlowMode() {
      if (!this.journeyId) {
        return;
      }
      try {
        const response = await this.backendApi('getProposalStatus', [this.journeyId], {}, 'get');
        if (!response || response === 'error' || response.status === 'error') {
          return;
        }
        const journeyData = response.data?.data;
        const flowMode = journeyData?.flowMode;
        const mode = String(flowMode || '').toLowerCase();
        this.prePaymentFlow = mode === 'pre_payment';
        this.postPaymentFlow = mode === 'post_payment';
        this.proposalSubmitted = this.isProposalSubmittedFlag(journeyData?.proposalSubmitted);
      } catch (error) {
        console.error('Failed to read journey flow mode:', error);
      }
    },
    manageOvd(mode) {
      try {
        const plan = this.planForDisplay;
        if (!plan?.insuranceAndProducts) {
          return false;
        }
        if (shouldSkipGodigitKycOvd(plan)) {
          return false;
        }
        const OVD_KYC = this.$store.state.configData?.OVD_KYC;
        const insurerCode = plan.insuranceAndProducts.insuranceCompanyCode;
        let kycResponse = sessionStorage.getItem('kyc_response');
        kycResponse = kycResponse ? JSON.parse(kycResponse) : null;
        const kycStatus = kycResponse?.kycInfo?.Status;
        const isNotApproved = kycStatus !== 'approved';
        const modeMapping = {
          BEFORE_PAYMENT: 'POST_PROPOSAL',
          AFTER_PAYMENT: 'POST_PAYMENT',
        };
        const requiredMode = modeMapping[mode];
        const configValue = OVD_KYC?.[insurerCode];
        return configValue === requiredMode && (!kycResponse || isNotApproved);
      } catch (error) {
        console.error('Failed to evaluate OVD after payment:', error);
        return false;
      }
    },
    resolvePersistedOutcome() {
      const fromQuery = this.$route.query?.[PAYMENT_STATUS_OUTCOME_QUERY_KEY];
      if (TERMINAL_OUTCOMES.includes(fromQuery)) {
        return fromQuery;
      }
      const fromSession = sessionStorage.getItem(PAYMENT_STATUS_OUTCOME_STORAGE_KEY);
      if (TERMINAL_OUTCOMES.includes(fromSession)) {
        return fromSession;
      }
      return null;
    },
    applyOutcomeView(outcome) {
      this.showSuccess = outcome === PAYMENT_STATUS_OUTCOME_SUCCESS;
      this.showFailure = outcome === PAYMENT_STATUS_OUTCOME_FAILURE;
      this.showInspection = outcome === PAYMENT_STATUS_OUTCOME_INSPECTION;
      this.showRequirements = outcome === PAYMENT_STATUS_OUTCOME_REQUIREMENTS;
      this.paymentPending = outcome === PAYMENT_STATUS_OUTCOME_PENDING;
      this.showError = false;
      this.errorMessage = '';
      this.completingAfterPayment = false;
      this.showingProposalSubmit = false;
      this.statusHint = '';
      this.stopProgressBar();
    },
    syncOutcomeQuery(outcome) {
      const query = {
        ...this.$route.query,
        [PAYMENT_STATUS_OUTCOME_QUERY_KEY]: outcome,
      };
      if (this.journeyId) {
        query.journeyId = this.journeyId;
      }
      if (
        this.$route.path === PAYMENT_STATUS_PATH
        && this.$route.query?.[PAYMENT_STATUS_OUTCOME_QUERY_KEY] === outcome
        && (!this.journeyId || String(this.$route.query?.journeyId) === String(this.journeyId))
      ) {
        return;
      }
      this.$router.replace({ path: PAYMENT_STATUS_PATH, query });
    },
    showOutcome(outcome) {
      if (!TERMINAL_OUTCOMES.includes(outcome)) {
        return;
      }
      sessionStorage.setItem(PAYMENT_STATUS_OUTCOME_STORAGE_KEY, outcome);
      this.applyOutcomeView(outcome);
      this.syncOutcomeQuery(outcome);
    },
    restoreSessionFromStorage() {
      try {
        if (sessionStorage.getItem('journey_id')) {
          this.journeyId = JSON.parse(sessionStorage.getItem('journey_id'));
          this.$store.commit('setJourneyId', this.journeyId);
        } else if (this.$route.query.journeyId || this.$route.query.journey_id) {
          const raw = this.$route.query.journeyId || this.$route.query.journey_id;
          this.journeyId = typeof raw === 'string' && raw.startsWith('"')
            ? JSON.parse(raw)
            : raw;
          sessionStorage.setItem('journey_id', JSON.stringify(this.journeyId));
          this.$store.commit('setJourneyId', this.journeyId);
        } else if (this.$store.state.journeyId) {
          this.journeyId = this.$store.state.journeyId;
        }

        this.configData = this.$store.state.configData || null;
        const planRaw =
          sessionStorage.getItem('selectedPlanForCheckout')
          || sessionStorage.getItem('selected_plan_data');
        if (planRaw) {
          this.selectedPlan = JSON.parse(planRaw);
        }
        const plan = this.planForDisplay;
        this.tpv =
          plan?.productDetails?.totalPremiumDetails?.[0]?.totalPremiumWithTax
          || plan?.totalPremiumDetails?.[0]?.totalPremiumWithTax
          || '';
        try {
          this.uiQuoteId = JSON.parse(sessionStorage.getItem('quoteRequestID')) || '';
        } catch {
          this.uiQuoteId = '';
        }
      } catch (err) {
        console.error('Failed to restore payment session:', err);
      }
    },
    parsePayResponseFromQuery() {
      try {
        const query = this.$route.query || {};
        // Preferred health-style return: referenceId + action=payment (or full query)
        if (query.action === 'payment' || query.referenceId || query.reference_id) {
          this.payResponse = { ...query };
          if (typeof this.payResponse.varFields === 'string') {
            try {
              this.payResponse.varFields = JSON.parse(
                decodeURIComponent(this.payResponse.varFields).replace(/'/g, '"'),
              );
            } catch (err) {
              console.error('Error parsing varFields param:', err);
            }
          }
          sessionStorage.setItem('pay_response', JSON.stringify(this.payResponse));
          return;
        }
        // Legacy motor blob
        if (query.response) {
          const tmpRes = query.response;
          const updatedRes = typeof tmpRes === 'string'
            ? tmpRes.replace(/'/g, '"')
            : JSON.stringify(tmpRes);
          this.payResponse = JSON.parse(updatedRes);
          sessionStorage.setItem('pay_response', JSON.stringify(this.payResponse));
          return;
        }
        this.payResponse = { ...query };
        if (Object.keys(this.payResponse).length > 0) {
          sessionStorage.setItem('pay_response', JSON.stringify(this.payResponse));
        }
      } catch (err) {
        console.error('Error parsing payment response query:', err);
      }
    },
    redirectToDiy(message) {
      this.showDiyError(message || this.errorMsgs || PAYMENT_GATEWAY_FALLBACK_MESSAGE);
    },
    showDiyError(message) {
      this.showError = true;
      this.errorMessage = message || PAYMENT_GATEWAY_FALLBACK_MESSAGE;
      this.paymentPending = false;
      this.completingAfterPayment = false;
      this.showingProposalSubmit = false;
      this.showSuccess = false;
      this.showFailure = false;
      this.showInspection = false;
      this.showRequirements = false;
      this.statusHint = '';
      this.stopProgressBar();
    },
    goToDiy() {
      const query = {};
      if (this.journeyId) {
        query.journeyId = this.journeyId;
      }
      this.$router.push({ path: DIY_CHECK_PATH, query });
    },
    showPaymentPending() {
      this.showOutcome(PAYMENT_STATUS_OUTCOME_PENDING);
      this.syncPaymentPendingQuery();
    },
    syncPaymentPendingQuery() {
      const query = {
        ...this.$route.query,
        [PAYMENT_STATUS_OUTCOME_QUERY_KEY]: PAYMENT_STATUS_OUTCOME_PENDING,
        status: PAYMENT_PENDING_QUERY_STATUS,
        statusDescription: PAYMENT_PENDING_QUERY_DESCRIPTION,
        action: PAYMENT_PENDING_QUERY_ACTION,
      };
      if (this.journeyId) {
        query.journeyId = this.journeyId;
      }
      this.$router.replace({ path: PAYMENT_STATUS_PATH, query });
    },
    isHttp503(value) {
      if (value == null) {
        return false;
      }
      const status = value?.response?.status ?? value?.status ?? value?.data?.status;
      return Number(status) === 503 || String(status) === '503';
    },
    async pollPaymentDetailsAndRedirect() {
      if (!this.journeyId) {
        this.redirectToDiy();
        return;
      }

      const reqData = [this.journeyId, PAYMENT_DETAILS_STAGE, 'false'];
      const headers = {};
      const endTime = Date.now() + PAYMENT_DETAILS_POLL_TIMEOUT_MS;

      try {
        while (Date.now() < endTime) {
          const response = await this.backendApi('getProposalWithStage', reqData, headers, 'get');

          if (this.isHttp503(response)) {
            this.showPaymentPending();
            return;
          }

          if (
            !response
            || response === 'error'
            || response.status === 'error'
            || response.data?.errors?.length > 0
          ) {
            const apiMessage = response?.data?.errors?.length
              ? response.data.errors
                  .map((element, index) => `${index + 1}. ${stripHtml(element.errorDisplayMessage)}`)
                  .join('<br/>')
              : PAYMENT_GATEWAY_FALLBACK_MESSAGE;
            this.redirectToDiy(apiMessage);
            return;
          }

          const data = response.data.data;
          const { outcome, paymentStatus } = classifyPaymentDetailsStatus(data);

          if (outcome === 'failure') {
            if (data?.proposal_id) {
              this.applicationNo = data.proposal_id;
              sessionStorage.setItem('applicationNumber', JSON.stringify(this.applicationNo));
            }
            sessionStorage.setItem('payment_poll_status', JSON.stringify(paymentStatus));
            sessionStorage.setItem('paymentDetails', JSON.stringify(data));
            this.payment_status = paymentStatus;
            this.showOutcome(PAYMENT_STATUS_OUTCOME_FAILURE);
            return;
          }

          if (outcome === 'success') {
            if (data?.proposal_id) {
              this.applicationNo = data.proposal_id;
              sessionStorage.setItem('applicationNumber', JSON.stringify(this.applicationNo));
            }
            sessionStorage.setItem('payment_poll_status', JSON.stringify(paymentStatus));
            sessionStorage.setItem('paymentDetails', JSON.stringify(data));
            this.payment_status = paymentStatus;
            this.completingAfterPayment = true;
            this.statusHint = 'Payment confirmed. Completing your proposal…';
            this.startProgressBar();
            try {
              await this.loadFlowMode();
              if (!this.proposalSubmitted) {
                await this.getInsurerFeedback();
              } else {
                await this.redirectAfterPaymentSuccess();
              }
            } catch (postSuccessError) {
              console.error('Error after payment success; showing success:', postSuccessError);
              this.showOutcome(PAYMENT_STATUS_OUTCOME_SUCCESS);
            }
            return;
          }

          if (outcome === 'verification_failed') {
            this.redirectToDiy();
            return;
          }

          await this.wait(PAYMENT_DETAILS_POLL_INTERVAL_MS);
        }

        this.showPaymentPending();
      } catch (error) {
        console.error('Unexpected error while checking payment details:', error);
        this.showPaymentPending();
      }
    },
    async refreshProposalSession() {
      try {
        const qData = [this.journeyId, 'Proposal Review', 'false'];
        const headers = { Authorization: `Bearer ${this.$store.state.token}` };
        const response = await this.backendApi('getProposalDetails', qData, headers, 'get');
        if (!response?.data?.data) {
          return null;
        }
        const payload = response.data.data;
        if (payload.user) {
          sessionStorage.setItem('rm_data', JSON.stringify(payload.user));
        }
        if (payload.proposal) {
          sessionStorage.setItem('propsalDetails_review', JSON.stringify(payload));
          sessionStorage.setItem('filledData', JSON.stringify(payload.proposal.proposal_json));
          if (payload.proposal.proposal_ui_id) {
            sessionStorage.setItem('proposal_ui_id', JSON.stringify(payload.proposal.proposal_ui_id));
          }
        }
        if (payload.quote_request?.quote_request_json) {
          sessionStorage.setItem('quote_request', JSON.stringify(payload.quote_request.quote_request_json));
        }
        if (payload.quote_response?.quote_response_data) {
          const pln = payload.quote_response.quote_response_data;
          sessionStorage.setItem('selected_plan_data', JSON.stringify(pln));
          sessionStorage.setItem('selectedPlanForCheckout', JSON.stringify(pln));
          sessionStorage.setItem('quoteSingleData', JSON.stringify([pln]));
          this.selectedPlan = pln;
        }
        return payload;
      } catch (error) {
        console.error('Failed to refresh proposal session after payment:', error);
        return null;
      }
    },
    resolveQuotedPremium(proposalResponse, plan) {
      const fromProposal =
        proposalResponse?.totalPremiumDetails?.[0]?.totalPremiumWithTax
        || proposalResponse?.productDetails?.totalPremiumDetails?.[0]?.totalPremiumWithTax;
      if (fromProposal != null && fromProposal !== '') {
        return fromProposal;
      }
      const planObj = plan || this.planForDisplay;
      return (
        planObj?.productDetails?.totalPremiumDetails?.[0]?.totalPremiumWithTax
        || planObj?.totalPremiumDetails?.[0]?.totalPremiumWithTax
        || ''
      );
    },
    ensureThankYouSessionDefaults(proposalResponse) {
      if (!sessionStorage.getItem('policy_status') || sessionStorage.getItem('policy_status') === 'undefined') {
        sessionStorage.setItem('policy_status', JSON.stringify('Underwriting'));
      }
      if (!sessionStorage.getItem('payment_poll_status') || sessionStorage.getItem('payment_poll_status') === 'undefined') {
        sessionStorage.setItem('payment_poll_status', JSON.stringify(this.payment_status || 'success'));
      }

      let plan = this.planForDisplay;
      try {
        if (!plan && sessionStorage.getItem('selected_plan_data')) {
          plan = JSON.parse(sessionStorage.getItem('selected_plan_data'));
        }
      } catch {
        plan = null;
      }

      const policyStatus = (() => {
        try {
          return JSON.parse(sessionStorage.getItem('policy_status')) || 'Underwriting';
        } catch {
          return 'Underwriting';
        }
      })();

      const premium = this.resolveQuotedPremium(proposalResponse, plan);
      const existingRaw = sessionStorage.getItem('manufacturer_paymentStatus');
      let existing = null;
      try {
        existing = existingRaw && existingRaw !== 'undefined' ? JSON.parse(existingRaw) : null;
      } catch {
        existing = null;
      }

      if (!existing) {
        const stub = {
          productCode: plan?.insuranceAndProducts?.productCode || plan?.productCode || '',
          policyDetails: {
            policyStatus,
            policyNumber: this.policyNo || proposalResponse?.ManufacturerTracking?.policyNumber || '',
            policyEffectiveDate: proposalResponse?.policy?.policyStartDate || null,
            policyEndDate: proposalResponse?.policy?.policyEndDate || null,
          },
          premiumStatus: premium !== '' && premium != null
            ? [{ totalPremiumPaid: premium }]
            : [],
          applicationStatus: { applicationStatus: policyStatus },
        };
        sessionStorage.setItem('manufacturer_paymentStatus', JSON.stringify(stub));
        return;
      }

      if (!existing.premiumStatus?.[0]?.totalPremiumPaid && premium !== '' && premium != null) {
        existing.premiumStatus = [{ totalPremiumPaid: premium }];
        if (!existing.policyDetails) {
          existing.policyDetails = { policyStatus };
        } else if (!existing.policyDetails.policyStatus) {
          existing.policyDetails.policyStatus = policyStatus;
        }
        if (!existing.productCode && plan?.insuranceAndProducts?.productCode) {
          existing.productCode = plan.insuranceAndProducts.productCode;
        }
        sessionStorage.setItem('manufacturer_paymentStatus', JSON.stringify(existing));
      }
    },
    async hydrateThankYouSession() {
      await this.refreshProposalSession();

      let proposalResponse = null;
      try {
        const existingSubmitted = sessionStorage.getItem('propsalSubmited_afterPayment');
        if (existingSubmitted && existingSubmitted !== 'undefined') {
          proposalResponse = JSON.parse(existingSubmitted);
        }
      } catch {
        proposalResponse = null;
      }

      try {
        const reqData = [this.journeyId, 'Proposal Submitted', 'false'];
        const headers = {};
        const response = await this.backendApi('getProposalWithStage', reqData, headers, 'get');
        if (
          response
          && response !== 'error'
          && response.status !== 'error'
          && !(response.data?.errors?.length > 0)
        ) {
          const pr = response.data?.data?.proposal_response;
          if (pr && String(pr.status?.code) === '200') {
            proposalResponse = pr;
            sessionStorage.setItem('propsalSubmited_afterPayment', JSON.stringify(pr));
            if (response.data?.data?.proposal_details) {
              sessionStorage.setItem('afterProposal_payment', JSON.stringify(response.data.data.proposal_details));
            }
            if (pr?.ManufacturerTracking?.policyNumber) {
              this.policyNo = pr.ManufacturerTracking.policyNumber;
              this.inspectionLink = pr?.inspectionDetails?.selfInspectionLink;
              const inspectionvalidTill = pr?.inspectionDetails?.inspectionValidTill;
              sessionStorage.setItem('inspectionLink', this.inspectionLink);
              sessionStorage.setItem('inspectionvalidTill', inspectionvalidTill);
              sessionStorage.setItem('policyNo', this.policyNo);
            }
            if (pr?.ManufacturerTracking?.applicationNumber && !sessionStorage.getItem('applicationNumber')) {
              sessionStorage.setItem(
                'applicationNumber',
                JSON.stringify(pr.ManufacturerTracking.applicationNumber),
              );
            }
          }
        }
      } catch (error) {
        console.error('Failed to hydrate Proposal Submitted for thank-you:', error);
      }

      this.ensureThankYouSessionDefaults(proposalResponse);
    },
    async showThankYouAfterPollGap() {
      await this.hydrateThankYouSession();
      this.showOutcome(PAYMENT_STATUS_OUTCOME_SUCCESS);
    },
    async redirectAfterPaymentSuccess() {
      this.progressPercent = 100;
      this.stopProgressBar();
      if (this.manageOvd('AFTER_PAYMENT')) {
        this.$router.push('/upload-ovds?nextRef=payment');
        return;
      }
      await this.getProposalStatus();
    },
    async getInsurerFeedback() {
      try {
        if (this.prePaymentFlow) {
          this.showingProposalSubmit = true;
          this.completingAfterPayment = false;
          this.verifyingStage = 'submitting_proposal';
          this.statusHint = SUBMITTING_HINT;
        } else {
          this.showingProposalSubmit = false;
          this.completingAfterPayment = true;
          this.verifyingStage = 'confirming_payment';
          this.statusHint = CONFIRMING_HINT;
        }

        const self = this;
        self.errorMsgs = '';
        const endTime = Date.now() + PAYMENT_DETAILS_POLL_TIMEOUT_MS;
        const reqData = [this.journeyId, 'Proposal Submitted', 'false'];
        const headers = {};

        this.refreshProposalSession();

        while (Date.now() < endTime) {
          try {
            const response = await this.backendApi('getProposalWithStage', reqData, headers, 'get');
            if (this.isHttp503(response)) {
              this.showPaymentPending();
              return;
            }
            if (response.data.errors.length === 0) {
              const proposalResponse = response.data.data.proposal_response;
              if (proposalResponse !== null && proposalResponse.errors && proposalResponse.errors.length > 0) {
                let msg = '';
                proposalResponse.errors.forEach((element, index) => {
                  msg += `${index + 1}. ${stripHtml(element.errorDisplayMessage)}<br/>`;
                });
                this.redirectToDiy(msg);
                return;
              }
              if (proposalResponse !== null && String(proposalResponse.status?.code) === '200') {
                sessionStorage.setItem('propsalSubmited_afterPayment', JSON.stringify(proposalResponse));
                sessionStorage.setItem('afterProposal_payment', JSON.stringify(response.data.data.proposal_details));

                if (proposalResponse?.ManufacturerTracking?.policyNumber) {
                  this.policyNo = proposalResponse.ManufacturerTracking.policyNumber;
                  this.inspectionLink = proposalResponse?.inspectionDetails?.selfInspectionLink;
                  const inspectionvalidTill = proposalResponse?.inspectionDetails?.inspectionValidTill;
                  sessionStorage.setItem('inspectionLink', this.inspectionLink);
                  sessionStorage.setItem('inspectionvalidTill', inspectionvalidTill);
                  sessionStorage.setItem('policyNo', this.policyNo);
                }

                if (String(this.payment_status).toLowerCase() === 'success') {
                  this.statusHint = 'Payment successful, getting final status…';
                  if (this.manageOvd('AFTER_PAYMENT')) {
                    this.$router.push('/upload-ovds?nextRef=payment');
                    return;
                  }
                  await this.getProposalStatus();
                  return;
                }
                if (String(this.payment_status).toLowerCase() === 'failed') {
                  this.showOutcome(PAYMENT_STATUS_OUTCOME_FAILURE);
                  return;
                }
                this.showOutcome(PAYMENT_STATUS_OUTCOME_SUCCESS);
                return;
              }
            }
            if (response.data.errors.length > 0) {
              let msg = '';
              response.data.errors.forEach((element, index) => {
                msg += `${index + 1}. ${stripHtml(element.errorDisplayMessage)}<br/>`;
              });
              this.redirectToDiy(msg);
              return;
            }
          } catch (apiError) {
            if (this.isHttp503(apiError)) {
              this.showPaymentPending();
              return;
            }
          }
          await this.wait(PAYMENT_DETAILS_POLL_INTERVAL_MS);
        }

        this.redirectToDiy();
      } catch (error) {
        console.error('An error occurred during insurer feedback:', error);
        this.showPaymentPending();
      }
    },
    async getProposalStatus() {
      try {
        this.statusHint = 'Please wait, getting information…';
        const self = this;
        self.errorMsgs = '';
        const reqData = [this.journeyId];
        const headers = {};
        const response = await this.backendApi('getPolicyStatus', reqData, headers, 'get');
        const statusRequestId = response.data.reqId;
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach((element, index) => {
            self.errorMsgs += `${index + 1}. ${stripHtml(element.errorDisplayMessage)}<br/>`;
          });
        }
        await this.pollPaymentStatus(statusRequestId);
      } catch (error) {
        console.error('An error occurred during policy status fetch:', error);
        this.redirectToDiy();
      }
    },
    async pollPaymentStatus(statusRequestId) {
      const startTime = Date.now();
      const maxDuration = PAYMENT_DETAILS_POLL_TIMEOUT_MS;

      try {
        const reqData = [this.journeyId, statusRequestId];

        while (Date.now() - startTime < maxDuration) {
          const response = await this.backendApi('paymentPollStatus', reqData, {}, 'get');
          const responseData = response?.data?.data;
          const errors = response?.data?.errors || [];

          // Poll errors (e.g. insurer token expired) — hydrate then thank-you
          if (errors.length > 0) {
            await this.showThankYouAfterPollGap();
            return;
          }

          if (responseData?.isPollComplete) {
            const product = responseData?.manufacturer?.[0]?.product?.[0];
            if (!product) {
              await this.showThankYouAfterPollGap();
              return;
            }

            this.policyNo = product.policyDetails?.policyNumber;
            sessionStorage.setItem('policyNo', this.policyNo);
            if (responseData.applicationNumber) {
              sessionStorage.setItem('applicationNumber', JSON.stringify(responseData.applicationNumber));
            } else {
              sessionStorage.setItem('applicationNumber', JSON.stringify(responseData.applicationNo));
            }
            sessionStorage.setItem(
              'policy_status',
              JSON.stringify(product.applicationStatus?.applicationStatus),
            );
            sessionStorage.setItem(
              'manufacturer_paymentStatus',
              JSON.stringify(product),
            );

            const applicationStatus = product.applicationStatus?.applicationStatus;

            if (['Policy Issued', 'Scrutiny', 'Underwriting'].includes(applicationStatus)) {
              this.showOutcome(PAYMENT_STATUS_OUTCOME_SUCCESS);
              return;
            }
            if (applicationStatus === 'Inspection Pending') {
              this.showOutcome(PAYMENT_STATUS_OUTCOME_INSPECTION);
              return;
            }
            if (applicationStatus === 'Requirements Pending') {
              this.showOutcome(PAYMENT_STATUS_OUTCOME_REQUIREMENTS);
              return;
            }
            this.showOutcome(PAYMENT_STATUS_OUTCOME_SUCCESS);
            return;
          }

          await this.wait(PAYMENT_DETAILS_POLL_INTERVAL_MS);
        }

        this.showPaymentPending();
      } catch (error) {
        console.error('Error in pollPaymentStatus:', error);
        await this.showThankYouAfterPollGap();
      }
    },
  },
};
</script>

<style scoped>
.payment-status-page,
.wrapper,
.inner-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.payment-status-insurer-logo {
  width: 100px;
  height: 50px;
  object-fit: contain;
}

.payment-status-content {
  padding: 4rem 1.5rem 3rem;
  max-width: min(48rem, 100%);
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-wrap: anywhere;
}

.payment-status-title {
  margin-bottom: 0.5rem;
}

.payment-status-subtitle {
  margin-bottom: 1rem;
  color: #6c757d;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payment-status-title--error,
.payment-status-subtitle--error {
  color: #dc3545;
}

.payment-status-countdown {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #212529;
}

.payment-status-progress {
  height: 10px;
  max-width: 420px;
  background-color: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.payment-status-progress .progress-bar {
  height: 100%;
  background-color: #0d6efd;
  transition: width 0.2s linear;
}

.payment-status-hint {
  font-size: 0.9rem;
  color: #6c757d;
}
</style>
